import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    InsertTable,
    linkDialogPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { useCallback, useContext, useEffect, useId, useMemo, useState } from "react"
import ReactPlayer from "react-player"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as CameraIcon } from "../assets/images/icons/ic-camera.svg"
import { ReactComponent as LiveIcon } from "../assets/images/icons/ic-live.svg"
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg"
import { ButtonStatus, FileType, LessonType } from "../common/constants"
import MainContext from "../common/MainContext"
import { canAccessPage } from "../common/permissions"
import Button from "../components/Button"
import Checkbox from "../components/Checkbox"
import SimpleCollapsable from "../components/SimpleCollapsable"
import TextInput from "../components/TextInput"
import styles from "./LessonEditor.module.css"
import TabItem from './TabItem'
import Uploader from './Uploader'


const LessonEditor = ({ lesson, module_id, onSave = () => { }, onCancel, onDelete = () => { } }) => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState('info')
    const [copyMessage, setCopyMessage] = useState('')
    const tabId = useId()
    const { user } = useContext(MainContext)
    const [disableSave, setDisableSave] = useState(true)
    const canEditLessons = useMemo(() => canAccessPage(user, 'lessons', 'edit'), [user])

    const [lessonEdit, setLessonEdit] = useState({ ...lesson, mandatory: lesson?.mandatory ?? true, to_process: lesson?.to_process ?? true })
    const [applyThumbnailToEdition, setApplyThumbnailToEdition] = useState(false)
    const [tags, setTags] = useState([])
    const [saveStatus, setSaveStatus] = useState(null)
    const [reopenStatus, setReopenStatus] = useState(null)
    const [uploaderReset, setUploaderReset] = useState(false)
    const [suggestedTime, setSuggestedTime] = useState(null)
    const [videoDuration, setVideoDuration] = useState(null)
    const videoUrl = lessonEdit.video_url?.trim() || null
    const finishedAt = lessonEdit.terminated_at || lessonEdit.ended_at
    const finishedDate = finishedAt ? new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(finishedAt) ? finishedAt : `${finishedAt}Z`) : null
    const finishedLabel = finishedDate && !Number.isNaN(finishedDate.getTime())
        ? finishedDate.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
        : finishedAt

    const copyIdentifier = async (label, value) => {
        try {
            await navigator.clipboard.writeText(String(value))
            setCopyMessage(`${label} copiato`)
        } catch {
            setCopyMessage('Copia non riuscita. Seleziona e copia il valore manualmente.')
        }
    }

    useEffect(() => {
        if (!copyMessage) return
        const timeout = setTimeout(() => setCopyMessage(''), 3000)
        return () => clearTimeout(timeout)
    }, [copyMessage])
    const [syncEndWithStart, setSyncEndWithStart] = useState(!lesson?.ends_at)

    const isPastLesson = useMemo(() => {
        if (!lessonEdit?.id) {
            return false
        }

        const lessonEndReference = lesson?.ended_at || lesson?.ends_at || lessonEdit?.ended_at || lessonEdit?.ends_at
        if (!lessonEndReference) {
            return false
        }

        const endDate = new Date(lessonEndReference)
        if (Number.isNaN(endDate.getTime())) {
            return false
        }

        return endDate < new Date()
    }, [lesson, lessonEdit?.id, lessonEdit?.ended_at, lessonEdit?.ends_at])

    const canReopenLesson = useMemo(() => {
        if (!lessonEdit?.id || !lessonEdit?.terminated_at || !lessonEdit?.ends_at) {
            return false
        }

        const terminatedAt = new Date(`${lessonEdit.terminated_at}Z`)
        const endsAt = new Date(`${lessonEdit.ends_at}Z`)
        if (Number.isNaN(terminatedAt.getTime()) || Number.isNaN(endsAt.getTime())) {
            return false
        }

        return terminatedAt < endsAt && endsAt > new Date()
    }, [lessonEdit?.id, lessonEdit?.terminated_at, lessonEdit?.ends_at])


    useEffect(() => {
        const getTags = async (type) => {
            let tags = await api.get(`/admin/tags?type=${type ?? 'general'}`)
            setTags(tags)
        }

        getTags()

    }, [])

    const getSuggestedTimes = useCallback(async () => {
        try {
            let suggestedTimes = await api.get(`/admin/lessons/utils/suggested-times?module_id=${module_id}`)
            setSuggestedTime(suggestedTimes.length > 0 ? suggestedTimes[0] : null)
        }
        catch (e) {
            console.error(e)
        }
    }, [module_id])

    useEffect(() => {
        if (!canEditLessons) {
            setDisableSave(true)
            return
        }

        if (isPastLesson) {
            setDisableSave(lessonEdit.name.trim() === '')
            return
        }

        if (lessonEdit.type === LessonType.Live) {
            setDisableSave(
                lessonEdit.name.trim() === '' ||
                lessonEdit.thumbnail?.trim() === ''
            )
        } else {
            setDisableSave(
                lessonEdit.name.trim() === '' ||
                lessonEdit.description.trim() === '' ||
                lessonEdit.thumbnail?.trim() === '' ||
                lessonEdit.video_url?.trim() === '' ||
                !lessonEdit.video_duration
            )
        }
    }, [canEditLessons, isPastLesson, lessonEdit])

    useEffect(() => {
        if (!module_id) {
            setSuggestedTime(null)
            return
        }
        getSuggestedTimes()
    }, [module_id, getSuggestedTimes])

    const update = async () => {
        if (!canEditLessons) {
            return
        }
        setSaveStatus(ButtonStatus.Loading)
        try {
            if (isPastLesson) {
                const res = await api.put(`/admin/lessons/${lessonEdit.id}/past-metadata`, {
                    name: lessonEdit.name,
                    description: lessonEdit.description ?? '',
                    thumbnail: lessonEdit.thumbnail,
                    apply_thumbnail_to_edition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()),
                    mandatory: lessonEdit.mandatory,
                    to_process: lessonEdit.to_process
                })
                onSave(res, { applyThumbnailToEdition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()) })
                setSaveStatus(ButtonStatus.Success)
                return
            }

            const updatedLesson = {
                name: lessonEdit.name,
                description: lessonEdit.description,
                thumbnail: lessonEdit.thumbnail,
                apply_thumbnail_to_edition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()),
                tags: lessonEdit.tags ? lessonEdit.tags.map(t => t.id) : null,
                mandatory: lessonEdit.mandatory,
                to_process: lessonEdit.to_process
            }

            if (lessonEdit.type === LessonType.Live) {
                updatedLesson.starts_at = lessonEdit.starts_at
                updatedLesson.ends_at = lessonEdit.ends_at
            }

            if (lessonEdit.type === LessonType.Recorded) {
                updatedLesson.video_url = lessonEdit.video_url
                updatedLesson.video_duration = lessonEdit.video_duration
            }

            if (lessonEdit.video_url) {
                updatedLesson.video_url = lessonEdit.video_url
                updatedLesson.video_duration = lessonEdit.video_duration
            }

            if (lessonEdit.subtitles) {
                updatedLesson.subtitles = lessonEdit.subtitles
            }

            const res = await api.put(`/admin/lessons/${lessonEdit.id}`, updatedLesson)
            onSave(res, { applyThumbnailToEdition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()) })
            setSaveStatus(ButtonStatus.Success)
            setUploaderReset(!uploaderReset)
        } catch (e) {
            console.error(e)
            setSaveStatus(ButtonStatus.Error)
        }
    }

    const create = async () => {
        if (!canEditLessons) {
            return
        }
        setSaveStatus(ButtonStatus.Loading)
        try {
            const newLesson = {
                module_id: lessonEdit.module_id,
                name: lessonEdit.name,
                description: lessonEdit.description,
                thumbnail: lessonEdit.thumbnail,
                apply_thumbnail_to_edition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()),
                type: lessonEdit.type,
                tags: lessonEdit.tags.map(t => t.id),
                mandatory: lessonEdit.mandatory,
                to_process: lessonEdit.to_process
            }

            if (lessonEdit.type === LessonType.Live) {
                newLesson.starts_at = lessonEdit.starts_at
                newLesson.ends_at = lessonEdit.ends_at
            }

            if (lessonEdit.type === LessonType.Recorded) {
                newLesson.video_url = lessonEdit.video_url
                newLesson.video_duration = lessonEdit.video_duration
            }

            if (lessonEdit.subtitles) {
                newLesson.subtitles = lessonEdit.subtitles
            }

            const res = await api.post(`/admin/lessons`, newLesson)
            onSave(res, { applyThumbnailToEdition: applyThumbnailToEdition && Boolean(lessonEdit.thumbnail?.trim()) })
            setSaveStatus(ButtonStatus.Success)
            setUploaderReset(!uploaderReset)
        } catch (e) {
            console.error(e)
            setSaveStatus(ButtonStatus.Error)
        }
    }

    const save = async () => {
        if (!canEditLessons) {
            return
        }

        if (lessonEdit.id) {
            update()
        }
        else {
            create()
        }
    }

    const reopenLesson = async () => {
        if (!canEditLessons || !lessonEdit?.id) {
            return
        }

        setReopenStatus(ButtonStatus.Loading)
        try {
            const res = await api.put(`/admin/lessons/${lessonEdit.id}/reopen`)
            setLessonEdit((prev) => ({ ...prev, terminated_at: null }))
            onSave(res)
            setReopenStatus(ButtonStatus.Success)
        } catch (e) {
            console.error(e)
            setReopenStatus(ButtonStatus.Error)
        }
    }

    function setTimeOnIsoTimestamp(isoTimestamp, timeString) {
        if (!isoTimestamp || !timeString) {
            return null
        }

        const baseDate = new Date(`${isoTimestamp}Z`)
        if (Number.isNaN(baseDate.getTime())) {
            return null
        }

        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        const year = baseDate.getFullYear()
        const month = baseDate.getMonth()
        const day = baseDate.getDate()

        const utcDate = new Date(Date.UTC(year, month, day, hours || 0, minutes || 0, seconds || 0, 0))
        return utcDate.toISOString()
    }

    function formatUtcTimeToLocal(timeString, isoTimestamp) {
        if (!timeString) {
            return ''
        }

        const referenceDate = isoTimestamp ? new Date(`${isoTimestamp}Z`) : new Date()
        if (Number.isNaN(referenceDate.getTime())) {
            return ''
        }

        const [hours, minutes, seconds] = timeString.split(':').map(Number)
        const year = referenceDate.getFullYear()
        const month = referenceDate.getMonth()
        const day = referenceDate.getDate()

        const utcDate = new Date(Date.UTC(year, month, day, hours || 0, minutes || 0, seconds || 0, 0))
        return utcDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const handleVideoDuration = (seconds) => {
        const duration = Math.floor(seconds)
        if (!Number.isFinite(duration) || duration <= 0) {
            return
        }
        setVideoDuration(duration)
        setLessonEdit((prev) => {
            prev.video_duration = duration
            return { ...prev }
        })
    };

    const parseM3u8Duration = async (manifestUrl, depth = 0) => {
        if (!manifestUrl || depth > 3) {
            return null
        }

        const response = await fetch(manifestUrl)
        if (!response.ok) {
            return null
        }

        const content = await response.text()

        const extInfMatches = [...content.matchAll(/#EXTINF:([\d.]+)/g)]
        if (extInfMatches.length > 0) {
            const total = extInfMatches.reduce((acc, match) => {
                const value = Number(match[1])
                return acc + (Number.isFinite(value) ? value : 0)
            }, 0)
            const rounded = Math.round(total)
            return rounded > 0 ? rounded : null
        }

        const lines = content
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line && !line.startsWith('#'))

        const firstNestedManifest = lines.find((line) => line.toLowerCase().endsWith('.m3u8'))
        if (!firstNestedManifest) {
            return null
        }

        const nestedUrl = new URL(firstNestedManifest, manifestUrl).href
        return parseM3u8Duration(nestedUrl, depth + 1)
    }

    const uploadDir = useMemo(() => {
        if (!lesson) return 'lessons'
        if (typeof lesson.course === 'string' && typeof lesson.edition === 'string') {
            return `lessons/${lesson.course}/${lesson.edition}/${lesson.name}`
        }
        if (typeof lesson.course === 'object' && typeof lesson.edition === 'object') {
            return `lessons/${lesson.course.name}/${lesson.edition.name}/${lesson.name}`
        }
        return 'lessons'
    }, [lesson])

    return (
        <>
            <div className={styles.container}>
                <div className={styles.content}>
                    <div className={styles.tabs} role="tablist" aria-label="Sezioni della lezione">
                        {[{ id: 'info', label: 'Informazioni' }, { id: 'video', label: 'Video' }].map((tab, index) => (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                id={`${tabId}-${tab.id}-tab`}
                                aria-controls={`${tabId}-${tab.id}-panel`}
                                aria-selected={activeTab === tab.id}
                                tabIndex={activeTab === tab.id ? 0 : -1}
                                className={styles.tab}
                                onClick={() => setActiveTab(tab.id)}
                                onKeyDown={(event) => {
                                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
                                    event.preventDefault()
                                    const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index
                                    setActiveTab(next === 0 ? 'info' : 'video')
                                    event.currentTarget.parentElement.children[next].focus()
                                }}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabPanel} role="tabpanel" id={`${tabId}-info-panel`} aria-labelledby={`${tabId}-info-tab`} hidden={activeTab !== 'info'}>
                    <div className={styles.row} style={{ display: 'flex', justifyContent: 'center' }}>
                        {
                            !lessonEdit.id &&
                            <div className={styles.typeContainer}>
                                <div className={styles.typeSwitch}>
                                    <TabItem
                                        selected={lessonEdit.type === LessonType.Live}
                                        text={"Live"}
                                        IconComponent={LiveIcon}
                                        color={"var(--sf-red-rgb)"}
                                        style={{ padding: ".5rem" }}
                                        onClick={() => {
                                            if (!canEditLessons) {
                                                return
                                            }
                                            setLessonEdit(l => ({ ...l, type: LessonType.Live }))
                                        }}
                                    />
                                    <TabItem
                                        selected={lessonEdit.type === LessonType.Recorded}
                                        text={"On demand"}
                                        IconComponent={CameraIcon}
                                        color={"var(--primary-rgb)"}
                                        style={{ padding: ".5rem" }}
                                        onClick={() => {
                                            if (!canEditLessons) {
                                                return
                                            }
                                            setLessonEdit(l => ({ ...l, type: LessonType.Recorded }))
                                        }}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                    <div className={styles.row}>
                        <div className={styles.left}>
                            <div className={styles.details}>
                                <h3 className={styles.sectionHeading}>Dettagli della lezione</h3>
                                <div className={styles.metadata}>
                                    {[{ label: 'ID lezione', value: lessonEdit.id }, { label: 'ID stanza', value: lessonEdit.room_name }]
                                        .filter(({ value }) => value !== null && value !== undefined && value !== '')
                                        .map(({ label, value }) => (
                                            <div className={styles.metadataField} key={label}>
                                                <span>{label}</span>
                                                <div className={styles.identifier}>
                                                    <code>{value}</code>
                                                    <button type="button" aria-label={`Copia ${label}`} title={`Copia ${label}`} onClick={() => copyIdentifier(label, value)}>
                                                        <CopyIcon aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    {finishedAt && (
                                        <div className={styles.metadataField}>
                                            <span>Terminata il</span>
                                            <time dateTime={finishedDate && !Number.isNaN(finishedDate.getTime()) ? finishedDate.toISOString() : undefined}>{finishedLabel}</time>
                                        </div>
                                    )}
                                </div>
                                {copyMessage && <small role="status">{copyMessage}</small>}
                                <TextInput appearance="modern"
                                    label="Nome della lezione"
                                    placeholder={"Nome"}
                                    type={"text"}
                                    disabled={!canEditLessons}
                                    value={lessonEdit.name}
                                    onKeyUp={v => setLessonEdit((prev) => {
                                        if (!canEditLessons) {
                                            return prev
                                        }
                                        prev.name = v
                                        return { ...prev }
                                    })}
                                />
                                <div className={styles.settings}>
                                <Checkbox
                                    size="1.25rem"
                                    radius="5px"
                                    label="Obbligatoria"
                                    labelPosition="end"
                                    value={lessonEdit.mandatory}
                                    disabled={!canEditLessons}
                                    onChange={(mandatory) => setLessonEdit((prev) => ({ ...prev, mandatory }))}
                                />
                                <Checkbox
                                    size="1.25rem"
                                    radius="5px"
                                    label="Processa"
                                    labelPosition="end"
                                    value={lessonEdit.to_process}
                                    disabled={!canEditLessons}
                                    onChange={(to_process) => setLessonEdit((prev) => ({ ...prev, to_process }))}
                                />
                                </div>
                                {/* <div className={styles.tags}>
                                    <TagSelector
                                        selected={lessonEdit.tags?.filter(t => t.type === TagType.General)}
                                        options={tags.filter(t => t.type === TagType.General)}
                                        placeholder={"Cerca tag"}
                                        onAdd={(item) => {
                                            const newOptions = tags.filter(t => t.id !== item.id)
                                            setTags([...newOptions])

                                            setLessonEdit((prev) => {
                                                if (!prev.tags.map(t => t.id).includes(item.id)) {
                                                    prev.tags.push(item)
                                                }
                                                return { ...prev }
                                            })
                                        }}
                                        onRemove={(item) => {
                                            const newOptions = [...tags]
                                            if (!newOptions.map(t => t.id).includes(item.id)) {
                                                newOptions.push(item)
                                            }
                                            setTags([...newOptions])

                                            setLessonEdit((prev) => {
                                                prev.tags = prev.tags.filter(t => t.id !== item.id)
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                </div> */}
                            </div>
                            <SimpleCollapsable expanded={lessonEdit.type === LessonType.Live}>
                                <div className={styles.schedule}>
                                    <h3 className={styles.sectionHeading}>Programmazione</h3>

                                    <div className={styles.date}>
                                        <TextInput appearance="modern"
                                            type="date"
                                            disabled={!canEditLessons || isPastLesson || (lesson.id && lesson.room_name && new Date(lesson.ends_at) < new Date())}
                                            value={lessonEdit.starts_at ? new Date(lessonEdit.starts_at + 'Z') : null}
                                            defaultValue={null}
                                            placeholder="Inizio Lezione"
                                            onKeyUp={(d) => {
                                                setLessonEdit((prev) => {
                                                    if (!canEditLessons) {
                                                        return prev
                                                    }
                                                    if (!d || !(d instanceof Date)) {
                                                        prev.starts_at = null
                                                        if (syncEndWithStart) {
                                                            prev.ends_at = null
                                                        }
                                                        return { ...prev }
                                                    }
                                                    const iso = d.toISOString().split('.')[0]
                                                    prev.starts_at = iso
                                                    if (syncEndWithStart) {
                                                        prev.ends_at = iso
                                                    }
                                                    return { ...prev }
                                                })
                                            }}
                                        />
                                        <TextInput appearance="modern"
                                            type="date"
                                            disabled={!canEditLessons || isPastLesson || (lesson.id && lesson.room_name && new Date(lesson.ends_at) < new Date())}
                                            value={lessonEdit.ends_at ? new Date(lessonEdit.ends_at + 'Z') : null}
                                            defaultValue={null}
                                            placeholder="Fine Lezione"
                                            onKeyUp={(d) => {
                                                setLessonEdit((prev) => {
                                                    if (!canEditLessons) {
                                                        return prev
                                                    }
                                                    if (!d || !(d instanceof Date)) {
                                                        prev.ends_at = null
                                                        setSyncEndWithStart(true)
                                                        return { ...prev }
                                                    }
                                                    prev.ends_at = d.toISOString().split('.')[0]
                                                    setSyncEndWithStart(false)
                                                    return { ...prev }
                                                })
                                            }}
                                        />
                                    </div>
                                    {suggestedTime && suggestedTime.start_time && suggestedTime.end_time && !lesson.starts_at && !lesson.ends_at &&
                                        <div className={styles.suggestedTimes}>
                                            Orario suggerito
                                            <Button appearance='text'
                                                disabled={!canEditLessons || isPastLesson || !lessonEdit.starts_at || !lessonEdit.ends_at}
                                                onClick={() => {
                                                    if (!canEditLessons) {
                                                        return
                                                    }
                                                    let starts_at = setTimeOnIsoTimestamp(lessonEdit.starts_at, suggestedTime.start_time)
                                                    let ends_at = setTimeOnIsoTimestamp(lessonEdit.ends_at, suggestedTime.end_time)
                                                    if (!starts_at || !ends_at) {
                                                        return
                                                    }
                                                    setLessonEdit((prev) => {
                                                        prev.starts_at = starts_at.split('.')[0]
                                                        prev.ends_at = ends_at.split('.')[0]
                                                        return { ...prev }
                                                    })
                                                }}>
                                                {formatUtcTimeToLocal(suggestedTime.start_time, lessonEdit.starts_at)} - {formatUtcTimeToLocal(suggestedTime.end_time, lessonEdit.ends_at)}
                                            </Button>
                                        </div>
                                    }
                                </div>
                            </SimpleCollapsable>
                            <section className={styles.descriptionSection}>
                                <div className={styles.cardContent} style={{ gap: 0 }}>
                                    <h3 className={styles.descriptionHeading}>Descrizione</h3>
                                    <MDXEditor
                                        className={styles.editor}
                                        markdown={lessonEdit.description}
                                        readOnly={!canEditLessons}
                                        onChange={(value) => {
                                            if (!canEditLessons) {
                                                return
                                            }
                                            setLessonEdit((prev) => {
                                                prev.description = value
                                                return { ...prev }
                                            })
                                        }}
                                        plugins={[
                                            headingsPlugin(),
                                            listsPlugin(),
                                            quotePlugin(),
                                            thematicBreakPlugin(),
                                            linkDialogPlugin(),
                                            diffSourcePlugin(),
                                            tablePlugin(),
                                            toolbarPlugin({
                                                toolbarContents: () => (
                                                    <>
                                                        {' '}
                                                        <UndoRedo />
                                                        <Separator />
                                                        <BoldItalicUnderlineToggles />
                                                        <Separator />
                                                        <ListsToggle />
                                                        <CreateLink />
                                                        <InsertTable />
                                                        <Separator />
                                                        <BlockTypeSelect />
                                                        <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                                    </>
                                                )
                                            })
                                        ]}
                                    />
                                </div>
                            </section>
                        </div>
                        <div className={styles.right}>
                            <div className={styles.thumbnailSection}>
                                <h3 className={styles.sectionHeading}>Immagine della lezione</h3>

                                <TextInput appearance="modern"
                                    disabled={!canEditLessons || lessonEdit?.thumbnail?.startsWith('blob:')}
                                    label={"URL immagine"}
                                    placeholder={"URL immagine"}
                                    type="text" value={lessonEdit?.thumbnail} onKeyUp={(value) => {
                                        setLessonEdit((prev) => {
                                            if (!canEditLessons) {
                                                return prev
                                            }
                                            prev.thumbnail = value ? value : ''
                                            return { ...prev }
                                        })
                                    }} />
                                {canEditLessons &&
                                    <Uploader
                                        crop={true}
                                        shape="rect"
                                        aspect={16 / 9}
                                        preview={lessonEdit?.thumbnail}
                                        iconButtons={true}
                                        reset={uploaderReset}
                                        multiple={false}
                                        type={FileType.Image}
                                        onComplete={(uploads) => {
                                            setLessonEdit((prev) => {
                                                prev.thumbnail = uploads[0].url
                                                return { ...prev }
                                            })
                                        }}
                                        onDelete={() => {
                                            setLessonEdit((prev) => {
                                                prev.thumbnail = ""
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                }
                                <Checkbox
                                    size="1.25rem"
                                    radius="5px"
                                    label="Applica l’immagine a tutte le lezioni dell’edizione"
                                    labelPosition="end"
                                    value={applyThumbnailToEdition}
                                    disabled={!canEditLessons || !lessonEdit.thumbnail?.trim()}
                                    onChange={setApplyThumbnailToEdition}
                                />
                                {applyThumbnailToEdition && <small>Al salvataggio, questa immagine sostituirà quella di tutte le lezioni passate e future dell’edizione.</small>}
                            </div>
                        </div>
                    </div>
                    </div>
                    <div className={styles.tabPanel} role="tabpanel" id={`${tabId}-video-panel`} aria-labelledby={`${tabId}-video-tab`} hidden={activeTab !== 'video'}>
                    {!(videoUrl || lesson.terminated_at || lesson.ended_at || lessonEdit.type === LessonType.Recorded) && (
                        <div className={styles.emptyVideo}>Il video e i sottotitoli saranno disponibili al termine della lezione live.</div>
                    )}
                    {(videoUrl || lesson.terminated_at || lesson.ended_at || lessonEdit.type === LessonType.Recorded) &&
                        <div className={styles.row}>
                            <div className={styles.videoSection}>
                                <h3 className={styles.sectionHeading}>Video e sottotitoli</h3>
                                {videoUrl && activeTab === 'video' && (
                                    <div className={styles.videoPreview}>
                                        <ReactPlayer
                                            url={videoUrl}
                                            controls
                                            playsinline
                                            width="100%"
                                            height="100%"
                                            onDuration={handleVideoDuration}
                                        />
                                    </div>
                                )}

                                <TextInput appearance="modern"
                                    label={"Video URL"}
                                    placeholder={"Video URL"}
                                    disabled={!canEditLessons || isPastLesson || lessonEdit?.video_url?.startsWith('blob:')}

                                    type="text" value={lessonEdit?.video_url} onKeyUp={(value) => {
                                        setLessonEdit((prev) => {
                                            if (!canEditLessons) {
                                                return prev
                                            }
                                            prev.video_url = value ? value : ''
                                            return { ...prev }
                                        })
                                    }} />
                                <TextInput appearance="modern"
                                    label={"Durata (s)"}
                                    placeholder={"Durata (s)"}
                                    disabled={!canEditLessons || isPastLesson || !videoUrl}

                                    type="text" value={videoDuration} onKeyUp={(value) => {
                                        setLessonEdit((prev) => {
                                            if (!canEditLessons) {
                                                return prev
                                            }
                                            prev.video_duration = value ? value : null
                                            return { ...prev }
                                        })
                                    }} />
                                {canEditLessons && !isPastLesson &&
                                    <Uploader
                                        subdirectory={uploadDir}
                                        preview={lessonEdit?.video_url}
                                        iconButtons={true}
                                        add_timestamp={false}
                                        reset={uploaderReset}
                                        multiple={true}
                                        type={FileType.All}
                                        onComplete={async (uploads) => {
                                            //cerca m3u8
                                            const m3u8 = uploads.find(u => u.url.endsWith('.m3u8'))
                                            const nextVideoUrl = m3u8 ? m3u8.url : uploads[0].url
                                            setLessonEdit((prev) => {
                                                prev.video_url = nextVideoUrl
                                                return { ...prev }
                                            })

                                            if (m3u8?.url) {
                                                const m3u8Duration = await parseM3u8Duration(m3u8.url)
                                                if (m3u8Duration) {
                                                    setVideoDuration(m3u8Duration)
                                                    setLessonEdit((prev) => {
                                                        prev.video_duration = m3u8Duration
                                                        return { ...prev }
                                                    })
                                                }
                                            }
                                        }}
                                        onDelete={() => {
                                            setLessonEdit((prev) => {
                                                prev.video_url = null
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                }
                                <TextInput appearance="modern"
                                    label={"Sottotitoli"}
                                    placeholder={"Sottotitoli"}
                                    disabled={!canEditLessons || isPastLesson || !videoUrl}

                                    type="text" value={lessonEdit?.subtitles} onKeyUp={(value) => {
                                        setLessonEdit((prev) => {
                                            if (!canEditLessons) {
                                                return prev
                                            }
                                            prev.subtitles = value ? value : null
                                            return { ...prev }
                                        })
                                    }} />
                                {canEditLessons && !isPastLesson &&
                                    <Uploader
                                        subdirectory={uploadDir}
                                        preview={lessonEdit?.subtitles}
                                        iconButtons={true}
                                        reset={uploaderReset}
                                        multiple={false}
                                        type={FileType.All}
                                        onComplete={(uploads) => {
                                            setLessonEdit((prev) => {
                                                prev.subtitles = uploads[0].url
                                                return { ...prev }
                                            })
                                        }}
                                        onDelete={() => {
                                            setLessonEdit((prev) => {
                                                prev.subtitles = null
                                                return { ...prev }
                                            })
                                        }}
                                    />
                                }
                            </div>
                        </div>
                    }
                    </div>
                    <div className={styles.save}>
                        {onCancel && <Button appearance="subtle" requiresEdit={false} onClick={onCancel}>Annulla</Button>}
                        {canReopenLesson &&
                            <Button
                                permissionPage="lessons"
                                requiresEdit
                                inverse
                                status={reopenStatus}
                                disabled={!canEditLessons || reopenStatus === ButtonStatus.Loading}
                                onClick={reopenLesson}>
                                RIAPRI
                            </Button>
                        }
                        {lessonEdit.id && (new Date(lessonEdit.starts_at) > new Date() || !lessonEdit.starts_at) &&
                            <Button
                                permissionPage="lessons"
                                requiresEdit
                                inverse
                                accentColor={"var(--sf-red)"}
                                status={saveStatus}
                                disabled={disableSave || !canEditLessons}
                                onClick={() => {
                                    if (!canEditLessons) {
                                        return
                                    }
                                    onDelete()
                                }}>
                                ELIMINA
                            </Button>
                        }
                        <Button
                            permissionPage="lessons"
                            requiresEdit
                            status={saveStatus}
                            disabled={disableSave || !canEditLessons || saveStatus === ButtonStatus.Loading}
                            onClick={save}>
                            {t("lessons.save").charAt(0).toUpperCase() + t("lessons.save").slice(1)}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    )

}

export default LessonEditor
